import React, { useState } from 'react';
import { Container, Button, Dropdown, Badge } from 'react-bootstrap';
import {
  Film,
  Headphones,
  Image as ImageIcon,
  FileText,
  Calendar,
  Clock,
  Folder,
  Search,
  LogIn,
  LogOut,
  UploadCloud,
  Shield,
  Home,
  ChevronRight,
  Menu,
  X,
} from 'lucide-react';
import { useJubileeAuth } from './auth/JubileeAuthContext';
import jubileeLogo from '../../assets/img/Jubilee-Logo.jpeg';

export default function JubileeNav({
  activeView,
  onNavigate,
  onOpenLogin,
  onOpenUpload,
  breadcrumbExtra = null,
}) {
  const { currentUser, isAuthenticated, role, logout, canUpload, canManage } = useJubileeAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { key: 'home', label: 'Dashboard', icon: Home },
    { key: 'videos', label: 'Videos', icon: Film, color: '#dc2626' },
    { key: 'audio', label: 'Audio', icon: Headphones, color: '#4f46e5' },
    { key: 'images', label: 'Photos', icon: ImageIcon, color: '#d97706' },
    { key: 'documents', label: 'Documents', icon: FileText, color: '#059669' },
    { key: 'events', label: 'Events', icon: Calendar },
    { key: 'timeline', label: 'Timeline', icon: Clock },
    { key: 'collections', label: 'Collections', icon: Folder },
    { key: 'search', label: 'Search', icon: Search },
  ];

  const handleNav = (key) => {
    onNavigate(key);
    setMobileMenuOpen(false);
  };

  const getBreadcrumbLabel = () => {
    const item = navItems.find((n) => n.key === activeView);
    return item ? item.label : activeView;
  };

  return (
    <>
      {/* 1. JUBILEE SUB-NAVBAR */}
      <div className="jubilee-subnav">
        <Container fluid="xl" className="d-flex align-items-center justify-content-between py-2 px-3">
          {/* Left: Brand Badge & Active Indicator */}
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-2"
              onClick={() => handleNav('home')}
            >
              <img
                src={jubileeLogo}
                alt="Jubilee Logo"
                className="rounded-circle shadow-xs"
                style={{ width: '32px', height: '32px', objectFit: 'cover', border: '1.5px solid #d4af37' }}
                onError={(e) => {
                  e.target.src = process.env.PUBLIC_URL + '/assets/img/Jubilee-Logo.jpeg';
                }}
              />
              <span className="jubilee-badge-pill">
                Platinum Jubilee
              </span>
              <span className="fw-bold text-dark d-none d-md-inline small">
                Archive Portal
              </span>
            </button>
          </div>

          {/* Center: Desktop Navigation Links */}
          <div className="d-none d-lg-flex align-items-center gap-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeView === item.key;
              return (
                <button
                  key={item.key}
                  className={`jubilee-nav-link btn btn-link ${isActive ? 'active' : ''}`}
                  onClick={() => handleNav(item.key)}
                >
                  <Icon size={15} style={{ color: isActive ? '#fff' : item.color || 'inherit' }} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Right: Auth, Upload & Admin Actions */}
          <div className="d-flex align-items-center gap-2">
            {isAuthenticated ? (
              <div className="d-flex align-items-center gap-2">
                {canUpload && (
                  <Button
                    variant="success"
                    size="sm"
                    className="rounded-pill d-flex align-items-center gap-1 fw-semibold shadow-xs"
                    onClick={onOpenUpload}
                  >
                    <UploadCloud size={15} />
                    <span className="d-none d-sm-inline">Upload</span>
                  </Button>
                )}

                {canManage && (
                  <Button
                    variant={activeView === 'admin' ? 'dark' : 'outline-dark'}
                    size="sm"
                    className="rounded-pill d-flex align-items-center gap-1 small"
                    onClick={() => handleNav('admin')}
                  >
                    <Shield size={14} />
                    <span className="d-none d-md-inline">Manage Archive</span>
                  </Button>
                )}

                <Dropdown align="end">
                  <Dropdown.Toggle
                    variant="light"
                    size="sm"
                    className="rounded-pill border d-flex align-items-center gap-2 py-1 px-2 shadow-none"
                  >
                    <img
                      src={currentUser?.avatar || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=100&q=80'}
                      alt=""
                      className="rounded-circle"
                      style={{ width: '22px', height: '22px', objectFit: 'cover' }}
                    />
                    <span className="small fw-semibold d-none d-sm-inline text-truncate" style={{ maxWidth: '100px' }}>
                      {currentUser?.name?.split(' ')[0]}
                    </span>
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="shadow-lg border-0 rounded-3 mt-2">
                    <div className="px-3 py-2 border-bottom">
                      <div className="fw-bold small text-dark">{currentUser?.name}</div>
                      <div className="text-muted text-xs">{currentUser?.email}</div>
                      <Badge bg="success" className="mt-1 text-xs">
                        Role: {role.toUpperCase()}
                      </Badge>
                    </div>
                    {canManage && (
                      <Dropdown.Item onClick={() => handleNav('admin')} className="small py-2 d-flex align-items-center gap-2">
                        <Shield size={14} className="text-primary" /> Manage Media & Categories
                      </Dropdown.Item>
                    )}
                    {canUpload && (
                      <Dropdown.Item onClick={onOpenUpload} className="small py-2 d-flex align-items-center gap-2">
                        <UploadCloud size={14} className="text-success" /> Upload Archival Record
                      </Dropdown.Item>
                    )}
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={logout} className="small py-2 text-danger d-flex align-items-center gap-2">
                      <LogOut size={14} /> Sign Out
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            ) : (
              /* Public Visitor: Clearly Visible Login Button */
              <Button
                variant="outline-success"
                size="sm"
                className="rounded-pill px-3 d-flex align-items-center gap-1 fw-semibold"
                onClick={onOpenLogin}
              >
                <LogIn size={15} />
                <span>Archivist Login</span>
              </Button>
            )}

            {/* Mobile Hamburger toggle */}
            <button
              className="btn btn-sm btn-light border d-lg-none p-2 rounded-3"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle navigation"
            >
              {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </Container>

        {/* Mobile Navigation Drawer */}
        {mobileMenuOpen && (
          <div className="d-lg-none bg-white border-top p-3 shadow-lg">
            <div className="d-flex flex-column gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeView === item.key;
                return (
                  <button
                    key={item.key}
                    className={`btn text-start d-flex align-items-center gap-2 py-2 px-3 rounded-3 small ${
                      isActive ? 'btn-success fw-bold' : 'btn-light text-dark'
                    }`}
                    onClick={() => handleNav(item.key)}
                  >
                    <Icon size={16} style={{ color: isActive ? '#fff' : item.color || 'inherit' }} />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* 2. BREADCRUMBS BAR (Rule #26: Important User Experience Rule - Always make it obvious where user is) */}
      <div className="jubilee-breadcrumb-bar">
        <Container fluid="xl">
          <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: '0.82rem' }}>
            <span className="text-secondary" style={{ cursor: 'pointer' }} onClick={() => handleNav('home')}>
              Home
            </span>
            <ChevronRight size={13} />
            <span className="text-secondary" style={{ cursor: 'pointer' }} onClick={() => handleNav('home')}>
              MDU Platinum Jubilee
            </span>
            <ChevronRight size={13} />
            <span className="fw-semibold text-dark text-capitalize">
              {getBreadcrumbLabel()}
            </span>
            {breadcrumbExtra && (
              <>
                <ChevronRight size={13} />
                <span className="text-success fw-semibold">{breadcrumbExtra}</span>
              </>
            )}
          </div>
        </Container>
      </div>
    </>
  );
}
